import { Plus, X } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/ConfirmDialog";
import { type Columns, DataTable } from "@/components/DataTable";
import { HintedButton } from "@/components/HintedButton";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { sessionQuery } from "@/features/auth/queries";
import { useSession } from "@/features/auth/useSession";
import { masterTypeIndexQuery } from "@/features/master-types/queries";
import {
  type AdminUser,
  type RoleGrant,
  activateUser,
  deactivateUser,
  revokeGrant,
} from "@/features/users/api";
import { ProvisionUserDialog } from "@/features/users/components/ProvisionUserDialog";
import { RoleGrantDialog } from "@/features/users/components/RoleGrantDialog";
import { usersQuery } from "@/features/users/queries";
import { ApiError } from "@/lib/api/problem";

/**
 * Where an admin provisions, activates, deactivates, and grants or
 * revokes roles.
 *
 * Split off DEE-13, whose backend shipped without this — see DEE-55. The
 * mutation surface here mirrors `mdm.auth.admin_service` exactly: an
 * activation with no grants and a scope on an administrative role are
 * both refused server-side, so the form declines to offer either rather
 * than round-tripping to find out.
 */
export const Route = createFileRoute("/_authed/users/")({
  staticData: { crumbs: [{ key: "nav.users" }] },
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(sessionQuery);
    await context.queryClient.ensureQueryData(usersQuery());
  },
  component: UsersAdmin,
});

function statusOf(user: AdminUser): "active" | "inactive" | "uncommissioned" {
  if (user.status === "active") return "active";
  if (user.status === "deactivated") return "inactive";
  return "uncommissioned";
}

function UsersAdmin() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { me, can } = useSession();
  // Not prefetched, not suspended on, and not assumed: `admin`/`super_admin`
  // hold no `master_type.read` by design (`mdm.auth.permissions._ADMIN`'s
  // "an admin is not a viewer"), so an admin acting on this page routinely
  // has none of this data. `scopeLabel` below falls back to the raw id
  // exactly the way `directory.nameFor` falls back to a raw uuid.
  const canReadMasterTypes = can("master_type.read");
  const { data: index } = useQuery({
    ...masterTypeIndexQuery(me.resolved_locale),
    enabled: canReadMasterTypes,
  });

  const [search, setSearch] = useState("");
  const [provisioning, setProvisioning] = useState(false);
  const [grantingFor, setGrantingFor] = useState<AdminUser | null>(null);
  const [deactivating, setDeactivating] = useState<AdminUser | null>(null);
  const [revoking, setRevoking] = useState<{ user: AdminUser; grant: RoleGrant } | null>(null);

  const query = useQuery(usersQuery());
  const rows = query.data ?? [];

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === "") return rows;
    return rows.filter(
      (row) =>
        row.email.toLowerCase().includes(needle) ||
        (row.display_name ?? "").toLowerCase().includes(needle),
    );
  }, [rows, search]);

  const invalidate = (): void => void queryClient.invalidateQueries({ queryKey: ["admin-users"] });

  const showError = (error: unknown, fallbackDetail?: string): void => {
    const problem = error instanceof ApiError ? error.problem : undefined;
    toast.error(problem?.title ?? t("errors.generic.title"), {
      description: problem?.detail ?? fallbackDetail ?? t("errors.generic.body"),
      duration: Number.POSITIVE_INFINITY,
    });
  };

  const activate = useMutation({
    mutationFn: (user: AdminUser) => activateUser(user.id),
    onSuccess: () => {
      toast.success(t("users.activated"));
      invalidate();
    },
    onError: (error) => showError(error),
  });

  const deactivate = useMutation({
    mutationFn: (user: AdminUser) => deactivateUser(user.id),
    onSuccess: () => {
      toast.success(t("users.deactivated"));
      setDeactivating(null);
      invalidate();
    },
    onError: (error) => {
      // 409 names exactly what is blocking it -- DEE-13's own remedy is
      // handover or reassignment, so the toast says so rather than
      // leaving the admin to guess what "assigned" means here.
      const detail =
        error instanceof ApiError && error.status === 409
          ? t("users.deactivateBlockedDetail")
          : undefined;
      showError(error, detail);
    },
  });

  const revoke = useMutation({
    mutationFn: (grantId: string) => revokeGrant(grantId),
    onSuccess: () => {
      toast.success(t("users.grant.revoked"));
      setRevoking(null);
      invalidate();
    },
    onError: (error) => showError(error),
  });

  const scopeLabel = useCallback(
    (grant: RoleGrant): string => {
      if (grant.master_type_id === null || grant.master_type_id === undefined) {
        return t("users.grant.global");
      }
      const type = index?.byId.get(grant.master_type_id);
      return type?.display_name[me.resolved_locale] ?? type?.code ?? grant.master_type_id;
    },
    [t, index, me.resolved_locale],
  );

  const columns = useMemo<Columns<AdminUser>>(
    () => [
      {
        id: "email",
        accessorKey: "email",
        header: t("users.columns.email"),
        cell: ({ row }) => (
          <span className="flex min-w-0 items-center gap-2.5">
            {/* Initials, per the design. `aria-hidden` because it is a
                restatement of the address beside it — announcing "D S"
                before the email is noise, not information. */}
            <span
              aria-hidden="true"
              className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-accent-surface font-caption font-medium text-accent-strong text-xs"
            >
              {initialsOf(row.original.display_name, row.original.email)}
            </span>
            {/* No `TruncatedText` here, for the reason measured in
                `master-types.index.tsx`: `DataTable` scrolls its own
                container rather than narrowing a cell, so nothing in a
                table cell actually clips and a tooltip attached here
                could never open. See the note there — PR #62 caps the
                pinned column below `lg`, which changes the answer for
                both of these cells once it lands. (DEE-52) */}
            <span className="block min-w-0">
              <span className="block truncate font-medium text-foreground">
                {row.original.email}
              </span>
              {row.original.display_name !== null && (
                <span className="block truncate text-muted text-xs">
                  {row.original.display_name}
                </span>
              )}
            </span>
          </span>
        ),
      },
      {
        id: "status",
        header: t("users.columns.status"),
        cell: ({ row }) => <StatusBadge status={statusOf(row.original)} />,
      },
      {
        id: "roles",
        header: t("users.columns.roles"),
        cell: ({ row }) => {
          const user = row.original;
          if (user.roles.length === 0) {
            return <span className="text-xs text-muted">{t("users.noRoles")}</span>;
          }
          return (
            <ul className="flex flex-wrap gap-1.5">
              {user.roles.map((grant) => (
                <li key={grant.grant_id}>
                  <span className="inline-flex min-h-11 items-center gap-1 rounded-full border border-border-strong bg-background pl-3 pr-1 text-foreground text-xs">
                    {t(`users.roles.${grant.role}`)}
                    <span className="text-muted">· {scopeLabel(grant)}</span>
                    {can("role.revoke") && (
                      <button
                        type="button"
                        onClick={() => setRevoking({ user, grant })}
                        aria-label={t("users.grant.revokeAction", {
                          role: t(`users.roles.${grant.role}`),
                          scope: scopeLabel(grant),
                        })}
                        // Was `size-4` — a 16x16 target, which is a WCAG
                        // 2.5.5 failure and half the width of the glyph's
                        // own tap area on a phone. It now fills the
                        // chip's 44px height; the painted circle stays
                        // small, the same trade `IconButton` makes.
                        className="-mr-0.5 inline-flex h-11 w-8 items-center justify-center rounded-full text-muted hover:text-foreground"
                      >
                        <X aria-hidden="true" className="size-3.5" />
                      </button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          );
        },
      },
      {
        id: "actions",
        header: t("users.columns.actions"),
        cell: ({ row }) => {
          const user = row.original;
          return (
            <div className="flex flex-wrap items-center gap-2">
              {can("role.grant") && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setGrantingFor(user)}
                  tooltip={t("tooltips.grantRole")}
                >
                  {t("users.grant.action")}
                </Button>
              )}
              {user.status === "deactivated" || user.status === "uncommissioned" ? (
                can("user.activate") ? (
                  user.roles.length === 0 ? (
                    <HintedButton size="sm" hint={t("users.activateNeedsGrant")}>
                      {t("users.activate")}
                    </HintedButton>
                  ) : (
                    <Button
                      size="sm"
                      disabled={activate.isPending}
                      onClick={() => activate.mutate(user)}
                      tooltip={t("tooltips.activateUser")}
                    >
                      {t("users.activate")}
                    </Button>
                  )
                ) : null
              ) : (
                can("user.deactivate") && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setDeactivating(user)}
                    tooltip={t("tooltips.deactivateUser")}
                  >
                    {t("users.deactivate")}
                  </Button>
                )
              )}
            </div>
          );
        },
      },
    ],
    [t, can, activate, scopeLabel],
  );

  return (
    <>
      <PageHeader
        title={t("users.title")}
        description={t("users.description")}
        help={t("pageHelp.users")}
        actions={
          can("user.provision") ? (
            <Button onClick={() => setProvisioning(true)} tooltip={t("tooltips.provisionUser")}>
              <Plus aria-hidden="true" />
              {t("users.provision.action")}
            </Button>
          ) : undefined
        }
      />

      <DataTable
        caption={t("users.title")}
        columns={columns}
        data={filtered}
        getRowId={(row) => row.id}
        isLoading={query.isPending}
        isFetching={query.isFetching}
        error={query.error}
        onRetry={() => void query.refetch()}
        search={{ value: search, onChange: setSearch }}
        onClearFilters={() => setSearch("")}
        activeFilters={
          search === "" ? [] : [{ field: "search", label: `${t("table.search")}: ${search}` }]
        }
        emptyTitle={t("users.empty.title")}
        emptyBody={t("users.empty.body")}
      />

      <ProvisionUserDialog open={provisioning} onOpenChange={setProvisioning} />
      <RoleGrantDialog
        open={grantingFor !== null}
        onOpenChange={(open) => !open && setGrantingFor(null)}
        user={grantingFor}
      />

      <ConfirmDialog
        open={deactivating !== null}
        onOpenChange={(open) => !open && setDeactivating(null)}
        title={t("users.confirmDeactivate.title")}
        description={t("users.confirmDeactivate.body", { email: deactivating?.email ?? "" })}
        destructive
        pending={deactivate.isPending}
        onConfirm={() => deactivating && deactivate.mutate(deactivating)}
      />

      <ConfirmDialog
        open={revoking !== null}
        onOpenChange={(open) => !open && setRevoking(null)}
        title={t("users.grant.confirmRevoke.title")}
        description={
          revoking
            ? t("users.grant.confirmRevoke.body", {
                role: t(`users.roles.${revoking.grant.role}`),
                scope: scopeLabel(revoking.grant),
                email: revoking.user.email,
              })
            : ""
        }
        destructive
        pending={revoke.isPending}
        onConfirm={() => revoking && revoke.mutate(revoking.grant.grant_id)}
      />
    </>
  );
}

/**
 * Up to two initials for the avatar.
 *
 * From the display name when there is one, otherwise the email's local
 * part — the same fallback the dashboard greeting uses, and for the same
 * reason: the full address is not a name.
 */
function initialsOf(displayName: string | null | undefined, email: string): string {
  const source = displayName?.trim() || (email.split("@")[0] ?? email);
  const words = source.split(/[\s._-]+/).filter((word) => word !== "");
  const letters = words.slice(0, 2).map((word) => word[0] ?? "");
  return (letters.join("") || source[0] || "?").toUpperCase();
}
